import { useCallback, useEffect, useState } from 'react'
import { checkAndGetInvoiceFromJwt } from '../requests/checkAndGetInvoiceFromJwt'
import { checkInvoiceIsPaid } from '../requests/checkInvoiceIsPaid'
import { getExistingInvoiceInfo } from '../requests/getExistingInvoiceInfo'
import {
  APPROVED_STATUSES,
  IntegrationErrorCodesType,
  IPayloadData,
  LoanApplicationStatuses,
  PROCESSING_STATUSES,
  unexpectedErrorCode,
} from '@linqpal/models/src/dictionaries'
import { getInvoiceCompanyInfo } from '../requests/getInvoiceCompanyInfo'
import { normalizeJwtInvoice } from '../helpers/normalizeJwtInvoice'

export const useCheckAndGetInvoicePayload = (jwtToken?: string) => {
  const [errorCode, setErrorCode] = useState<null | IntegrationErrorCodesType>(
    null,
  )
  const [invoice, setInvoice] = useState<null | any>(null)
  const [loading, setLoading] = useState<boolean>(true)
  const [invoicePaymentStatus, setInvoicePaymentStatus] = useState<
    null | string
  >(null)
  const [invoiceCreditStatus, setInvoiceCreditStatus] = useState<null | string>(
    null,
  )
  const [isInvoicePaid, setIsInvoicePaid] = useState<null | boolean>(null)

  const handleJwtInvoice = useCallback(
    async (payload: IPayloadData, existingInvoice?: any) => {
      const companyId = payload?.companyId

      if (!companyId) return setErrorCode(unexpectedErrorCode)

      try {
        const companyInfo = await getInvoiceCompanyInfo(companyId)
        if (!companyInfo) return setErrorCode(unexpectedErrorCode)

        const normalizedInvoice = normalizeJwtInvoice(
          payload,
          companyInfo,
          existingInvoice,
        )
        setInvoice(normalizedInvoice)
        setIsInvoicePaid(false)
      } catch (error) {
        console.error('Error handling JWT invoice:', error)
        setErrorCode(unexpectedErrorCode)
      }
    },
    [],
  )

  const handleInvoiceResponse = useCallback(
    async (res: any) => {
      const { errorCode: receivedErrorCode, data } = res || {}
      const { invoiceId, payload } = data || {}

      if (receivedErrorCode) return setErrorCode(receivedErrorCode)
      if (!invoiceId && !payload) return setErrorCode(unexpectedErrorCode)

      if (invoiceId) {
        const [checkInvoicePaid, existingInvoiceInfo] = await Promise.all([
          checkInvoiceIsPaid(invoiceId),
          getExistingInvoiceInfo(invoiceId),
        ])

        if (!checkInvoicePaid || !existingInvoiceInfo)
          return setErrorCode(unexpectedErrorCode)

        const isPaidByTradeCredit =
          existingInvoiceInfo?.currentInvoiceCreditStatus &&
          [...PROCESSING_STATUSES, ...APPROVED_STATUSES].includes(
            existingInvoiceInfo.currentInvoiceCreditStatus as LoanApplicationStatuses,
          )

        const invoiceIsPaid = checkInvoicePaid.isPaid || isPaidByTradeCredit
        setInvoicePaymentStatus(checkInvoicePaid.status)
        setInvoiceCreditStatus(existingInvoiceInfo.currentInvoiceCreditStatus)

        if (invoiceIsPaid) {
          setInvoice(existingInvoiceInfo.invoice)
          setIsInvoicePaid(true)
        } else {
          await handleJwtInvoice(payload, existingInvoiceInfo)
        }
      } else {
        await handleJwtInvoice(payload)
      }
    },
    [handleJwtInvoice],
  )

  useEffect(() => {
    const bootstrap = async () => {
      if (!jwtToken) return setErrorCode(unexpectedErrorCode)

      setLoading(true)
      const res = await checkAndGetInvoiceFromJwt(jwtToken)
      if (!res) {
        setErrorCode(unexpectedErrorCode)
        setLoading(false)
        return
      }

      await handleInvoiceResponse(res)
      setLoading(false)
    }

    bootstrap()
  }, [jwtToken, handleInvoiceResponse])

  return {
    errorCode,
    loading,
    invoice,
    invoicePaymentStatus,
    invoiceCreditStatus,
    isInvoicePaid,
  }
}
